import type { BrowserOptions, EventHint } from '@sentry/vue';
import { AxiosError } from 'axios';

// 1. Extract the exact function signature Sentry expects
// We wrap it in NonNullable because 'beforeSend' is optional in the interface
type BeforeSendHandler = NonNullable<BrowserOptions['beforeSend']>;

// 2. Define the function using that extracted type
export const sentryBeforeSend: BeforeSendHandler = (event, hint) => {
    // Sentry types hint as optional, but it's always present for caught errors
    const error = hint?.originalException;

    // authService wrappers (not AxiosErrors); ALL NEEDS_INTERACTIVE are dropped (interaction_required, login_required,
    // consent_required, 50199, AADSTS700084), matching jwtInterceptor.js:205 (DEVAMS-2175) - so a tenant-wide re-auth
    // policy change would not surface via this path. Avoids useMercure capture flooding Sentry (DEVAMS-2753).
    if (
        error &&
        typeof error === 'object' &&
        ['NEEDS_INTERACTIVE', 'CANCELLED'].includes((error as { type?: string }).type ?? '')
    ) {
        return null;
    }

    // Check if it is an Axios Error
    if (error && (error as AxiosError).isAxiosError) {
        const axiosError = error as AxiosError;

        // A. Filter Connection Issues
        // ERR_NETWORK = Network Error (Offline)
        // ECONNABORTED = Timeout
        // ERR_CANCELED = Request Cancelled by code/user
        if (['ERR_NETWORK', 'ECONNABORTED', 'ERR_CANCELED'].includes(axiosError.code || '')) {
            return null;
        }
        if (axiosError.message === 'Network Error') {
            return null;
        }

        // B. Filter Specific HTTP Status Codes
        if (axiosError.response) {
            const status = axiosError.response.status;

            // 400 = Bad Request
            // 401 = Unauthorized
            // 403 = Forbidden (Permission denied)
            // 404 = Not Found
            // 422 = Validation Error (Common in Symfony)
            // 429 = Too Many Requests (Rate limiting)
            if ([400, 401, 403, 404, 422, 429].includes(status)) {
                return null;
            }
        }
    }

    // --- 2. Filter Common Browser Noise (Non-Axios) ---
    // "ResizeObserver loop" is a benign warning common in Vue apps
    if (typeof event.message === 'string' && event.message.includes('ResizeObserver loop')) {
        return null;
    }

    // Stale chunk errors after a new deploy — handled by reloadOnStaleChunk() (DEVAMS-1844).
    // ChunkLoadError is the Webpack variant; the two below are Vite's native variants.
    const firstException = event.exception?.values?.[0]
    if (firstException?.type === 'ChunkLoadError') {
        return null;
    }
    if (
        firstException?.type === 'TypeError' &&
        firstException?.value?.includes('Failed to fetch dynamically imported module')
    ) {
        return null;
    }
    if (
        firstException?.type === 'Error' &&
        firstException?.value?.includes('Unable to preload CSS for')
    ) {
        return null;
    }


    return event;
};
